import Icon from '@amzn/awsui-components-core/icon';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { NetworkCardData, MetricDetail } from '../../mocks/executionMetricsData';

import './NetworkCard.scss';

interface NetworkCardProps {
  data: NetworkCardData;
  onSelect: () => void;
}

/**
 * Card status derives from the off-track metric COUNT (not the OTD
 * threshold, which is one metric among many):
 *   0 off track → green, 1 → amber, 2+ → red.
 */
type CardStatus = 'on-track' | 'at-risk' | 'off-track';

function cardStatus(offTrackCount: number): CardStatus {
  if (offTrackCount === 0) return 'on-track';
  return offTrackCount === 1 ? 'at-risk' : 'off-track';
}

/** "On-time Delivery (72.4% vs 80%)" — metric with the why in brackets. */
const formatOffTrackMetric = (metric: MetricDetail): string => {
  if (metric.value != null && metric.target != null) {
    return `${metric.name} (${metric.value}${metric.unit || ''} vs ${metric.target}${metric.unit || ''})`;
  }
  if (metric.value != null) {
    return `${metric.name}: ${metric.value}${metric.unit || ''}`;
  }
  return metric.name;
};

const NetworkCard = ({ data, onSelect }: NetworkCardProps) => {
  const offTrackMetrics = data.metrics.filter((m) => !m.isOnTrack);
  const status = cardStatus(offTrackMetrics.length);

  return (
    <div
      className={`network-card network-card--${status}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="network-card__header">
        <span className="network-card__name">{data.name}</span>
      </div>

      <div className="network-card__body">
        {offTrackMetrics.length > 0 ? (
          <SpaceBetween size="xxs">
            <div
              className={`network-card__status ${
                status === 'at-risk'
                  ? 'network-card__status--at-risk-indicator'
                  : 'network-card__status--off-track-indicator'
              }`}
            >
              <Icon
                name={status === 'at-risk' ? 'status-warning' : 'status-negative'}
                variant={status === 'at-risk' ? 'warning' : 'error'}
                size="small"
              />
              <span>{offTrackMetrics.length} Off track</span>
            </div>
            {/* Off-track metrics listed inline (value vs goal in brackets) —
                visible at a glance, no hover needed. */}
            <ul className="network-card__offtrack-list">
              {offTrackMetrics.map((metric) => (
                <li key={metric.name}>{formatOffTrackMetric(metric)}</li>
              ))}
            </ul>
          </SpaceBetween>
        ) : (
          <div className="network-card__status network-card__status--all-on-track">
            <Icon name="status-positive" variant="success" size="small" />
            <span>All metrics on track</span>
          </div>
        )}
        {data.callouts && (
          <div className="network-card__callouts">{data.callouts}</div>
        )}
      </div>
    </div>
  );
};

export default NetworkCard;
